import { useAutoAnimate } from "@formkit/auto-animate/react";
import { cn } from "@gymflow/helpers";
import { FormikProps, FormikProvider, FormikTouched } from "formik";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, ChevronRightIcon } from "../../atoms";
import { StepTabItem } from "./components/StepTabItem";

type StepType = {
  id: string;
  title: string;
  form: ReactNode;
};

type StepWizardNewProps<T> = {
  steps: StepType[];
  formik: FormikProps<T>;
};

export const StepWizardNew = <T,>({ steps, formik }: StepWizardNewProps<T>) => {
  const { t } = useTranslation();

  const [stepIndex, setStepIndex] = useState(0);

  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;

  useEffect(() => {
    const currentStepEl = stepRefs.current[stepIndex];
    const container = scrollContainerRef.current;
    if (currentStepEl && container) {
      currentStepEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [stepIndex]);

  const goToPrevious = useCallback(() => {
    if (!isFirst) setStepIndex((prev) => prev - 1);
  }, [isFirst]);

  const handleNextOrSave = useCallback(async () => {
    const errors = await formik.validateForm();
    const activeStepKey = steps[stepIndex].id as keyof T;
    const stepErrors = errors?.[activeStepKey];

    if (stepErrors && typeof stepErrors === "object" && stepErrors !== null) {
      const touchedFields = Object.keys(stepErrors) as Array<
        keyof T[typeof activeStepKey]
      >;

      const touchedObject: FormikTouched<T> = {
        [activeStepKey]: touchedFields.reduce(
          (acc, field) => {
            acc[field] = true as FormikTouched<
              T[typeof activeStepKey]
            >[typeof field];
            return acc;
          },
          {} as FormikTouched<T[typeof activeStepKey]>,
        ),
      } as FormikTouched<T>;

      formik.setTouched(touchedObject, true);
      return;
    }

    if (isLast) {
      await formik.submitForm();
    } else {
      setStepIndex((prev) => prev + 1);
    }
  }, [formik, isLast, stepIndex, steps]);

  const activeForm = steps[stepIndex].form;
  const [parent] = useAutoAnimate();
  return (
    <FormikProvider value={formik}>
      <div className="flex h-full max-h-full flex-col gap-6 overflow-y-hidden">
        <div
          className="flex flex-row overflow-x-auto rounded-xl border border-gray-200"
          ref={scrollContainerRef}
        >
          {steps.map(({ id, title }, index) => (
            <StepTabItem
              key={id}
              title={title}
              stepsLength={steps.length}
              index={index}
              activeStepIndex={stepIndex}
              stepRef={(el) => (stepRefs.current[index] = el)}
            />
          ))}
        </div>
        <div className="flex h-full max-h-full flex-col gap-4 overflow-y-auto">
          <div className="flex size-full max-h-full flex-col overflow-y-hidden rounded-2xl border border-gray-200">
            <div
              ref={parent}
              className="flex flex-1 flex-col gap-8 overflow-y-auto p-4 lg:p-6"
            >
              {activeForm}
            </div>
            <div
              className={cn(
                "flex flex-row justify-between gap-3 border-t border-t-gray-200 p-4 lg:px-6",
                { "justify-end": isFirst },
              )}
            >
              <Button
                intent="default"
                onClick={() => goToPrevious()}
                hidden={isFirst}
                className="w-36"
              >
                <div className="flex flex-row items-center justify-center gap-1.5">
                  <ChevronRightIcon
                    className="size-5 -scale-x-100"
                    pathClassName="stroke-gray-950"
                  />
                  {t("portal.common.previous")}
                </div>
              </Button>
              <Button
                intent="secondary"
                className="w-36"
                onClick={() => handleNextOrSave()}
              >
                {isLast ? (
                  t("portal.common.save")
                ) : (
                  <div className="flex flex-row items-center justify-center gap-1.5">
                    {t("portal.common.next")}
                    <ChevronRightIcon
                      className="size-5"
                      pathClassName="stroke-gray-0"
                    />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};
